(() => {
  if (window.__zepCartWarrantyInstalled) return;
  window.__zepCartWarrantyInstalled = true;

  const pending = new Set();
  const declinedPrefix = 'zep-warranty-declined:';
  let syncing = false;
  let syncRequested = false;

  const scheduleSync = () => {
    [250, 800, 1600].forEach((delay) => window.setTimeout(syncWarrantyLines, delay));
  };

  const cartRequest = async (path, body) => {
    const response = await fetch(path, {
      method: body ? 'POST' : 'GET',
      headers: body ? { 'Content-Type': 'application/json', Accept: 'application/json' } : { Accept: 'application/json' },
      body: body ? JSON.stringify(body) : undefined,
      credentials: 'same-origin'
    });
    if (!response.ok) throw new Error(`Cart request failed: ${response.status}`);
    return response.json();
  };

  const sectionRequest = (payload) => ({
    ...payload,
    sections: ['minicart'],
    sections_url: `${location.pathname}${location.search}`
  });

  const refreshDrawer = (response, eventName = 'drawer:Minicart') => {
    document.body.dispatchEvent(new CustomEvent(eventName, {
      bubbles: true,
      detail: { response, zepWarranty: true }
    }));
  };

  const showError = (offer) => {
    const status = offer?.querySelector('[data-zep-warranty-error]');
    if (!status) return;
    status.textContent = document.documentElement.lang?.toLowerCase().startsWith('ro')
      ? 'Nu am putut actualiza garantia. Incearca din nou.'
      : 'We could not update the warranty. Please try again.';
    status.hidden = false;
  };

  const findParent = (cart, parentKey, parentVariantId) => cart.items.find((item) =>
    !item.properties?._zep_warranty_for
    && (String(item.variant_id) === String(parentVariantId) || item.key === parentKey)
  );
  const attachedTo = (cart, parentKey, parentVariantId, warrantyVariantId) => cart.items.find((item) =>
    String(item.variant_id) === String(warrantyVariantId)
    && (String(item.properties?._zep_warranty_parent_variant) === String(parentVariantId)
      || item.properties?._zep_warranty_for === parentKey)
  );

  const addWarranty = async (button) => {
    const offer = button.closest('[data-zep-warranty-offer]');
    const root = button.closest('[data-zep-cart-warranty-root]');
    const parentKey = button.dataset.parentKey;
    const parentVariantId = button.dataset.parentVariantId;
    const warrantyVariantId = root?.dataset.warrantyVariantId;
    if (!offer || !parentKey || !parentVariantId || !warrantyVariantId || pending.has(parentVariantId)) return;

    pending.add(parentVariantId);
    button.disabled = true;
    try {
      const cart = await cartRequest('/cart.js');
      const parent = findParent(cart, parentKey, parentVariantId);
      if (!parent) return;
      if (attachedTo(cart, parentKey, parentVariantId, warrantyVariantId)) {
        await syncWarrantyLines();
        return;
      }

      const response = await cartRequest('/cart/add.js', sectionRequest({
        items: [{
          id: Number(warrantyVariantId),
          quantity: parent.quantity,
          properties: {
            _zep_warranty: 'true',
            _zep_warranty_for: parentKey,
            _zep_warranty_parent_product: button.dataset.parentProductId,
            _zep_warranty_parent_variant: button.dataset.parentVariantId
          }
        }]
      }));
      refreshDrawer(response, 'ajax:addToCart');
    } catch (error) {
      console.error('[ZEP warranty]', error);
      showError(offer);
    } finally {
      pending.delete(parentVariantId);
      if (button.isConnected) button.disabled = false;
    }
  };

  const removeWarranty = async (button) => {
    const offer = button.closest('[data-zep-warranty-offer]');
    const warrantyKey = button.dataset.warrantyKey;
    if (!warrantyKey || pending.has(warrantyKey)) return;
    pending.add(warrantyKey);
    button.disabled = true;
    try {
      const response = await cartRequest('/cart/change.js', sectionRequest({ id: warrantyKey, quantity: 0 }));
      if (response.items?.some((item) => item.key === warrantyKey)) {
        throw new Error('Warranty line was not removed');
      }
      refreshDrawer(response);
    } catch (error) {
      console.error('[ZEP warranty]', error);
      showError(offer);
    } finally {
      pending.delete(warrantyKey);
      if (button.isConnected) button.disabled = false;
    }
  };

  const declineWarranty = (button) => {
    const offer = button.closest('[data-zep-warranty-offer]');
    const parentKey = offer?.dataset.parentKey;
    if (parentKey) sessionStorage.setItem(`${declinedPrefix}${parentKey}`, '1');
    if (offer) offer.hidden = true;
  };

  const restoreDeclines = () => {
    document.querySelectorAll('[data-zep-warranty-offer]').forEach((offer) => {
      const key = offer.dataset.parentKey;
      if (key && sessionStorage.getItem(`${declinedPrefix}${key}`) === '1' && !offer.classList.contains('is-added')) {
        offer.hidden = true;
      }
    });
  };

  async function syncWarrantyLines() {
    if (syncing) {
      syncRequested = true;
      return;
    }
    syncing = true;
    try {
      const cart = await cartRequest('/cart.js');
      const updates = {};
      cart.items.forEach((warranty) => {
        const parentKey = warranty.properties?._zep_warranty_for;
        if (!parentKey) return;
        const parentVariantId = warranty.properties?._zep_warranty_parent_variant;
        const parent = findParent(cart, parentKey, parentVariantId);
        if (!parent) updates[warranty.key] = 0;
        else if (warranty.quantity !== parent.quantity) updates[warranty.key] = parent.quantity;
      });
      if (!Object.keys(updates).length) {
        restoreDeclines();
        return;
      }
      const response = await cartRequest('/cart/update.js', sectionRequest({ updates }));
      refreshDrawer(response);
    } catch (error) {
      console.error('[ZEP warranty sync]', error);
    } finally {
      syncing = false;
      if (syncRequested) {
        syncRequested = false;
        window.setTimeout(syncWarrantyLines, 0);
      }
    }
  }

  document.addEventListener('click', (event) => {
    const add = event.target.closest('[data-zep-warranty-add]');
    const remove = event.target.closest('[data-zep-warranty-remove]');
    const decline = event.target.closest('[data-zep-warranty-decline]');
    const cartControl = event.target.closest('#js_cart_popup .js_plus, #js_cart_popup .js_minus, #js_cart_popup .js-remove-item');
    if (cartControl) scheduleSync();
    if (!add && !remove && !decline) return;
    event.preventDefault();
    if (add) addWarranty(add);
    else if (remove) removeWarranty(remove);
    else declineWarranty(decline);
  });

  ['drawer:Minicart', 'ajax:updateCart', 'ajax:deleteCart', 'contentUpdated'].forEach((eventName) => {
    document.body.addEventListener(eventName, () => {
      restoreDeclines();
      scheduleSync();
    });
  });

  document.addEventListener('drawer:Minicart', scheduleSync);

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => {
      restoreDeclines();
      syncWarrantyLines();
    }, { once: true });
  } else {
    restoreDeclines();
    syncWarrantyLines();
  }
})();
